import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import Radom, { ICheckoutSession, OpenBankingPaymentView } from '../state/Radom'
import Spinner from '../components/Spinner'
import { Container } from '../components/Animations'
import BreadCrumbs from '../components/Breadcrumbs'
import PageHeader from '../components/PageHeader'
import CheckoutSessionDetails from '../components/CheckoutSessionDetails'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Modal from '../components/Modal'
import RefundForm from '../components/RefundForm'
import {
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  PageHeaderStack,
  Surface,
  SurfaceHeader,
  SurfaceSubtext,
  SurfaceTitle,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue
} from '../components/DashboardPrimitives'
import { PAY_BASE_URL, RADOM_COLORS } from '../util/Constants'
import LinkExternal from '../components/LinkExternal'
import { ExternalLinkText } from './Events'
import { usePermissions } from '../util/permissions'
import { sessionStatusLabel } from './CheckoutSessions'
import {
  effectiveSessionStatus,
  isOpenBankingPaymentInFlight,
  openBankingStatusPresentation,
  openBankingViewFromSession
} from '../util/OpenBankingPayment'

const formatDateTime = (value?: string): string | undefined => {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString()
}

/** One sentence on what the session status means for this session. */
const sessionStatusExplanation = (session: ICheckoutSession, status: string, bankPaymentStatus?: string): string => {
  const expiry = formatDateTime(session.expiresAt)
  const pastExpiry = !!session.expiresAt && new Date(session.expiresAt).getTime() < Date.now()
  switch (status) {
  case 'pending':
    if (isOpenBankingPaymentInFlight(bankPaymentStatus)) {
      return pastExpiry
        ? 'Kept open past its expiry time because the customer\'s bank payment is still completing'
        : 'The customer has approved a bank payment, which is still completing'
    }
    return expiry ? `Waiting for the customer to pay. Expires ${expiry}` : 'Waiting for the customer to pay'
  case 'success':
    return 'The customer paid'
  case 'cancelled':
    return 'Cancelled before a payment was made'
  case 'expired':
    return expiry ? `Expired ${expiry} without a completed payment` : 'Expired without a completed payment'
  case 'refunded':
    return 'Paid, then refunded to the customer'
  case 'refundFailed':
    return 'Paid. The last refund could not be delivered'
  default:
    return 'Current checkout session state'
  }
}

const CheckoutSession = observer((): ReactElement => {
  const params = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [checkoutSession, setCheckoutSession] = useState<ICheckoutSession>()
  const [bankPayment, setBankPayment] = useState<OpenBankingPaymentView>()

  const [refundModalOpen, setRefundModalOpen] = useState(false)
  const { has } = usePermissions()

  useEffect(() => {
    if (!params.id) {
      return
    }

    let cancelled = false
    setIsLoading(true)
    setBankPayment(undefined)
    Radom.getCheckoutSession(params.id)
      .then(s => {
        if (cancelled) return
        setCheckoutSession(s)
        if (!s.gateway?.openBanking && !s.openBankingPaymentData && !s.payment?.openBanking) return
        // The payment view carries the live status and the full history. The
        // page already shows what the session knows while it loads.
        Radom.getOpenBankingPayment(s.id)
          .then(view => { if (!cancelled) setBankPayment(view) })
          .catch(() => { /* an API build without the payment view: the session's own data stays */ })
      })
      .catch(e => console.error('Failed to look up checkout session', e))
      .finally(() => { if (!cancelled) setIsLoading(false) })

    return () => { cancelled = true }
  }, [params.id])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!checkoutSession) {
    return <></>
  }

  const lineItemsCount = checkoutSession.items?.length ?? 0
  const productsCount = checkoutSession.products?.length ?? 0
  const sessionHasPayment = !!checkoutSession.payment
  const shownBankPayment = bankPayment ?? openBankingViewFromSession(checkoutSession)
  const sessionStatus = effectiveSessionStatus(checkoutSession.sessionStatus, shownBankPayment?.status)
  const bankPaymentStatus = shownBankPayment ? openBankingStatusPresentation(shownBankPayment.status) : undefined

  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

    <PageHeaderRow>
      <PageHeaderStack>
        <BreadCrumbs items={[
          { item: 'Checkout sessions', to: '/checkout_sessions' },
          { item: checkoutSession.id, to: '.' }
        ]} />
        <PageHeader title='Checkout session' subTitle={checkoutSession.id} />
      </PageHeaderStack>

      <PageActionRow>
        {
          checkoutSession.payment && has('WriteRefunds') &&
          <Dropdown
            noChevron
            iconTrigger
            selectedContent={<Dots width={15} />}
            dropdownContent={
              <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                <DropdownItem onClick={() => setRefundModalOpen(true)}>Refund order</DropdownItem>
              </div>
            }
          />
        }
      </PageActionRow>
    </PageHeaderRow>

    <Surface style={{ gap: 16 }}>
      <SurfaceHeader>
        <div>
          <SurfaceTitle>Session overview</SurfaceTitle>
          <SurfaceSubtext>Hosted checkout session and payment context.</SurfaceSubtext>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {
            bankPaymentStatus
              ? <InlinePill $tone={bankPaymentStatus.tone}>
                Bank payment: {bankPaymentStatus.label.toLowerCase()}
              </InlinePill>
              : <InlinePill $tone={sessionHasPayment ? 'green' : 'neutral'}>
                {
                  sessionHasPayment
                    ? 'Payment attached'
                    : sessionStatus === 'pending' ? 'Awaiting payment' : 'No payment'
                }
              </InlinePill>
          }
        </div>
      </SurfaceHeader>

      <SummaryStrip style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <SummaryStripItem>
          <SummaryLabel>Session status</SummaryLabel>
          <SummaryValue>{sessionStatusLabel(sessionStatus)}</SummaryValue>
          <SummaryMeta>
            {sessionStatusExplanation(checkoutSession, sessionStatus, shownBankPayment?.status)}
          </SummaryMeta>
        </SummaryStripItem>
        <SummaryStripItem>
          <SummaryLabel>Created</SummaryLabel>
          <div style={{ minWidth: 0, fontSize: 14.5, fontWeight: 500, lineHeight: 1.35, color: RADOM_COLORS.BLACK }}>
            {new Date(checkoutSession.createdAt).toLocaleString()}
          </div>
          <SummaryMeta>Creation time for this checkout session</SummaryMeta>
        </SummaryStripItem>
        <SummaryStripItem>
          <SummaryLabel>Line items</SummaryLabel>
          <SummaryValue>{lineItemsCount}</SummaryValue>
          <SummaryMeta>{productsCount} catalog product{productsCount === 1 ? '' : 's'} attached</SummaryMeta>
        </SummaryStripItem>
        <SummaryStripItem>
          <SummaryLabel>Hosted checkout</SummaryLabel>
          <div style={{ minWidth: 0, fontSize: 14.5, fontWeight: 500, lineHeight: 1.35 }}>
            <ExternalLinkText href={`${PAY_BASE_URL}/checkout/${checkoutSession.id}`} target='_blank'>
              {checkoutSession.id}
              <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
            </ExternalLinkText>
          </div>
          <SummaryMeta>Open the public checkout page</SummaryMeta>
        </SummaryStripItem>
      </SummaryStrip>
    </Surface>

    <CheckoutSessionDetails
      checkoutSession={checkoutSession}
      bankPayment={shownBankPayment}
      netPayment={
        (
          (Number(checkoutSession.payment?.managed?.amount) ||
          Number(checkoutSession.payment?.managedTokenDelegatePayment?.amount) || 0) -
          Number(checkoutSession.payment?.managed?.networkFee || 0)
        ) *
        0.995
      }
    />

    {
      refundModalOpen &&
      <Modal visible={refundModalOpen} onClose={() => setRefundModalOpen(false)} title='Refund order'>
        <div style={{ width: 500, padding: '0 30px 30px 30px' }}>
          <RefundForm checkoutSession={checkoutSession} onRefund={() => setRefundModalOpen(false)} />
        </div>
      </Modal>
    }

  </Container>
})

export default CheckoutSession
